import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';
import noImage from '../../assets/no-image.webp';

const defaultGetPhotoSrc = (photo) => {
  if (!photo) return null;
  if (typeof photo === 'string') return photo;
  return photo.url || photo.src || null;
};

const defaultOnImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = noImage;
};

export default function PhotoLightbox({
  photos,
  initialIndex = 0,
  onClose,
  getPhotoSrc = defaultGetPhotoSrc,
  onImgError = defaultOnImgError,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [animated, setAnimated] = useState(false);
  const [rotation, setRotation] = useState(0);

  const touch = useRef({
    startX: 0, startY: 0,
    pinchDist0: 0, pinchScale0: 1,
    panTx0: 0, panTy0: 0,
    isPinching: false,
    swipeDx: 0,
  });

  const getDist = (a, b) => {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const resetTransform = (animate = true) => {
    setAnimated(animate);
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const navigate = useCallback((dir) => {
    setIndex((i) => {
      const next = i + dir;
      if (next < 0 || next >= photos.length) return i;
      return next;
    });
    resetTransform(false);
    setRotation(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') navigate(1);
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onTouchStart = (e) => {
    const t = touch.current;
    t.isPinching = false;
    t.swipeDx = 0;

    if (e.touches.length === 2) {
      t.isPinching = true;
      t.pinchDist0 = getDist(e.touches[0], e.touches[1]);
      t.pinchScale0 = scale;
    } else if (e.touches.length === 1) {
      t.startX = e.touches[0].clientX;
      t.startY = e.touches[0].clientY;
      t.panTx0 = tx;
      t.panTy0 = ty;
    }
    setAnimated(false);
  };

  const onTouchMove = (e) => {
    e.preventDefault();
    const t = touch.current;

    if (e.touches.length === 2 && t.isPinching) {
      const newDist = getDist(e.touches[0], e.touches[1]);
      const newScale = Math.min(6, Math.max(1, t.pinchScale0 * (newDist / t.pinchDist0)));
      setScale(newScale);
      if (newScale <= 1) { setTx(0); setTy(0); }
    } else if (e.touches.length === 1 && !t.isPinching) {
      const dx = e.touches[0].clientX - t.startX;
      const dy = e.touches[0].clientY - t.startY;

      if (scale > 1) {
        setTx(t.panTx0 + dx);
        setTy(t.panTy0 + dy);
      } else {
        t.swipeDx = dx;
        setTx(dx);
      }
    }
  };

  const onTouchEnd = () => {
    const t = touch.current;
    setAnimated(true);

    if (scale <= 1) {
      const threshold = 60;
      if (t.swipeDx < -threshold && index < photos.length - 1) {
        navigate(1);
      } else if (t.swipeDx > threshold && index > 0) {
        navigate(-1);
      } else {
        setTx(0);
      }
      t.swipeDx = 0;
    }

    if (scale < 1.1) {
      resetTransform(true);
    }

    t.isPinching = false;
  };

  const src = getPhotoSrc(photos[index]) || noImage;
  const canPrev = index > 0;
  const canNext = index < photos.length - 1;

  const overlay = (
    <div
      className="photo-lightbox-overlay fixed inset-0 z-[200] flex items-center justify-center w-screen min-h-[100dvh]"
      style={{ background: 'rgba(0,0,0,0.95)' }}
    >
      <button
        className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-95 transition-transform"
        onClick={onClose}
      >
        <X className="w-6 h-6" strokeWidth={2} />
      </button>

      <button
        className="absolute top-4 right-16 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-95 transition-transform"
        onClick={() => setRotation((r) => (r + 90) % 360)}
        title="Повернуть фото"
      >
        <RotateCw className="w-5 h-5" strokeWidth={2} />
      </button>

      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/40 text-white text-[13px] font-medium px-3 py-1 rounded-full">
          {index + 1} / {photos.length}
        </div>
      )}

      {canPrev && (
        <button
          className="absolute left-2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-95 transition-transform hidden-touch"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="w-6 h-6" strokeWidth={2} />
        </button>
      )}
      {canNext && (
        <button
          className="absolute right-2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-95 transition-transform hidden-touch"
          onClick={() => navigate(1)}
        >
          <ChevronRight className="w-6 h-6" strokeWidth={2} />
        </button>
      )}

      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ touchAction: 'none' }}
      >
        <img
          src={src}
          onError={onImgError}
          alt=""
          draggable={false}
          style={{
            maxWidth: '100vw',
            maxHeight: 'var(--app-visual-height, 100dvh)',
            objectFit: 'contain',
            transform: `rotate(${rotation}deg) scale(${scale}) translate(${tx / scale}px, ${ty / scale}px)`,
            transition: animated ? 'transform 0.25s ease' : 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        />
      </div>

      {photos.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => { setIndex(i); resetTransform(false); }}
              className="rounded-full transition-all"
              style={{
                width: i === index ? 20 : 7,
                height: 7,
                background: i === index ? '#fff' : 'rgba(255,255,255,0.4)',
              }}
            />
          ))}
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @media (pointer: coarse) { .hidden-touch { display: none !important; } }
      `}} />
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(overlay, document.body);
}
